'use client';

import { useState } from 'react';
import { nb } from '@/lib/nobreak';
import type { HeroVisualBase } from './types';

/**
 * HERO VISUAL: DASHBOARD. A wide "control room" panel (not a browser window): a header strip with a segmented switch
 * (Branch / Region / Loan officer), a row of horizontal bars per team that grow in sequence, and a small routing graphic that sends one
 * new lead to its branch, with a compliance stamp that lands last.
 *
 * Props (all from frontmatter v2.heroVisual, nothing is hard-coded copy):
 *   title      heading in the header strip
 *   caption    small label over the bars (e.g. "Rollout status")
 *   views      [{ label, rows: [{ name, level }] }]  level is a 0-100 BAR LENGTH ONLY (pure illustration, it prints no number)
 *   selected   index of the view that is on at first (default 0)
 *   lead       { title, detail }  the lead that is being routed
 *   routeLabel text under the routing graphic (e.g. "Routed to its branch")
 *   feed       one-line status shown in the footer
 *   stamp      { text, muted }  the compliance stamp
 * The switch really works (client state); the server HTML holds the selected view's text. Motion is transform/opacity/stroke-dashoffset
 * only and is off under prefers-reduced-motion.
 */
type Row = { name: string; level: number };
type View = { label: string; rows: Row[] };
type Props = HeroVisualBase & {
  title?: string;
  views?: View[];
  selected?: number;
  lead?: { title?: string; detail?: string };
  routeLabel?: string;
  feed?: string;
  stamp?: { text?: string; muted?: string };
} & Record<string, unknown>;

const CSS = `
.lpd{--d-card:var(--sk-card,#10122a);--d-card2:var(--sk-card-2,#161430);--d-ink:var(--sk-ink,#fff);--d-mut:var(--sk-mut,rgba(255,255,255,.64));--d-line:var(--sk-line,rgba(255,255,255,.14));--d-soft:var(--sk-soft,rgba(255,255,255,.07));--d-edge:var(--sk-edge,rgba(255,255,255,.1));--d-violet:#7c5cff;--d-track:rgba(255,255,255,.08);--d-on:#ffffff;--d-stamp:#b9a6ff;--d-stampbg:rgba(124,92,255,.16);--d-drop:var(--sk-drop,0 30px 60px -34px rgba(0,0,0,.9))}
html[data-skin-mode="light"] .lpd{--d-card:#ffffff;--d-card2:#faf8ff;--d-ink:#0b0d18;--d-mut:#4d5268;--d-line:rgba(11,13,24,.13);--d-soft:rgba(124,92,255,.08);--d-edge:rgba(124,92,255,.22);--d-track:rgba(11,13,24,.07);--d-on:#ffffff;--d-stamp:#4f2fd6;--d-stampbg:rgba(124,92,255,.12);--d-drop:0 26px 50px -30px rgba(60,40,140,.45)}
.lpd-panel{position:relative;overflow:hidden;border-radius:26px;background:linear-gradient(180deg,var(--d-card),var(--d-card2));color:var(--d-ink);box-shadow:var(--d-drop),0 0 0 1px var(--d-edge);animation:lpd-in .7s cubic-bezier(.2,.8,.2,1) both}
.lpd-panel::before{content:"";position:absolute;inset:0;pointer-events:none;border-radius:inherit;background:radial-gradient(60% 90% at 0% 0%,rgba(124,92,255,.22),transparent 60%),radial-gradient(50% 80% at 100% 100%,rgba(34,228,255,.12),transparent 60%)}
.lpd-head{position:relative;display:flex;align-items:center;justify-content:space-between;gap:16px;padding:16px 22px;border-bottom:1px solid var(--d-line)}
.lpd-title{display:flex;align-items:center;gap:12px;min-width:0;font-family:var(--font-display),ui-sans-serif,system-ui,sans-serif;font-weight:700;font-size:16px;letter-spacing:-.01em;color:var(--d-ink)}
.lpd-glyph{display:grid;grid-template-columns:repeat(2,7px);gap:3px;flex:none}
.lpd-glyph i{display:block;width:7px;height:7px;border-radius:2px;background:var(--d-violet)}
.lpd-glyph i:nth-child(2){opacity:.55}.lpd-glyph i:nth-child(3){opacity:.35}.lpd-glyph i:nth-child(4){background:linear-gradient(135deg,#ff34a7,#22e4ff)}
.lpd-seg{display:inline-flex;gap:2px;padding:3px;border-radius:999px;background:var(--d-soft);box-shadow:inset 0 0 0 1px var(--d-line);flex:none}
.lpd-seg button{appearance:none;border:0;cursor:pointer;border-radius:999px;padding:7px 15px;font:600 13px/1 var(--font-text),ui-sans-serif,system-ui,sans-serif;color:var(--d-mut);background:transparent;transition:background-color .25s,color .25s;white-space:nowrap}
.lpd-seg button:hover{color:var(--d-ink)}
.lpd-seg button[aria-pressed="true"]{background:var(--d-violet);color:var(--d-on);box-shadow:0 6px 16px -6px rgba(124,92,255,.7)}
.lpd-seg button:focus-visible{outline:2px solid var(--d-violet);outline-offset:2px}
.lpd-body{position:relative;display:grid;grid-template-columns:minmax(0,1.3fr) minmax(0,1fr);gap:0}
.lpd-bars{padding:20px 22px 18px}
.lpd-cap{font:600 12px/1 var(--font-text),ui-sans-serif,system-ui,sans-serif;letter-spacing:.09em;text-transform:uppercase;color:var(--d-mut);margin:0 0 14px}
.lpd-row{display:grid;grid-template-columns:104px minmax(0,1fr);align-items:center;gap:12px;padding:7px 0}
.lpd-name{font:600 14px/1.2 var(--font-text),ui-sans-serif,system-ui,sans-serif;color:var(--d-ink);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.lpd-track{position:relative;height:14px;border-radius:999px;background:var(--d-track);overflow:hidden}
.lpd-fill{position:absolute;inset:0;border-radius:999px;transform-origin:left center;background:linear-gradient(90deg,#8b5cff,#7c5cff 55%,#22e4ff);animation:lpd-grow .9s cubic-bezier(.2,.8,.2,1) both}
.lpd-fill::after{content:"";position:absolute;inset:0;background:linear-gradient(90deg,transparent 0,rgba(255,255,255,.55) 50%,transparent 100%);transform:translateX(-120%);animation:lpd-sheen 5.5s ease-in-out 2.2s infinite}
.lpd-route{position:relative;padding:20px 22px 18px 6px;border-left:1px solid var(--d-line);background:linear-gradient(180deg,transparent,var(--d-soft))}
.lpd-rgrid{display:grid;grid-template-columns:158px 40px minmax(0,1fr);align-items:stretch;height:136px}
.lpd-lead{align-self:center;border-radius:14px;padding:10px 12px;background:var(--d-card);box-shadow:inset 0 0 0 1px var(--d-edge);margin-left:14px}
.lpd-lead b{display:flex;align-items:center;gap:8px;font:700 13px/1.25 var(--font-display),ui-sans-serif,system-ui,sans-serif;color:var(--d-ink)}
.lpd-lead span.d{position:relative;flex:none;width:8px;height:8px;border-radius:50%;background:#ff34a7}
.lpd-lead span.d::after{content:"";position:absolute;inset:0;border-radius:50%;background:#ff34a7;animation:lpd-ping 2.4s cubic-bezier(0,0,.2,1) infinite}
.lpd-lead em{display:block;white-space:nowrap;margin-top:5px;font:400 12px/1.35 var(--font-text),ui-sans-serif,system-ui,sans-serif;font-style:normal;color:var(--d-mut)}
.lpd-svg{width:100%;height:100%;display:block;overflow:visible}
.lpd-svg path{fill:none;vector-effect:non-scaling-stroke}
.lpd-line{stroke:var(--d-line);stroke-width:2}
.lpd-live{stroke:var(--d-violet);stroke-width:2.5;stroke-linecap:round;stroke-dasharray:6 7;animation:lpd-flow 1.6s linear infinite}
.lpd-dests{display:grid;grid-template-rows:repeat(3,1fr);gap:0}
.lpd-dest{display:flex;align-items:center;font:600 13px/1 var(--font-text),ui-sans-serif,system-ui,sans-serif;color:var(--d-mut)}
.lpd-dest span{padding:6px 11px;border-radius:999px;box-shadow:inset 0 0 0 1px var(--d-line);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%}
.lpd-dest.on{color:var(--d-ink)}
.lpd-dest.on span{background:var(--d-soft);box-shadow:inset 0 0 0 1.5px var(--d-violet)}
.lpd-rcap{margin:14px 0 0 14px;font:400 12px/1.35 var(--font-text),ui-sans-serif,system-ui,sans-serif;color:var(--d-mut)}
.lpd-foot{position:relative;display:flex;align-items:center;justify-content:space-between;gap:16px;padding:14px 22px;border-top:1px solid var(--d-line);background:var(--d-soft)}
.lpd-feed{display:flex;align-items:center;gap:10px;min-width:0;font:500 14px/1.3 var(--font-text),ui-sans-serif,system-ui,sans-serif;color:var(--d-ink)}
.lpd-feed i{flex:none;width:8px;height:8px;border-radius:50%;background:var(--d-violet)}
.lpd-stamp{flex:none;display:inline-flex;align-items:center;gap:9px;padding:8px 15px 8px 11px;border-radius:12px;color:var(--d-stamp);background:var(--d-stampbg);box-shadow:inset 0 0 0 2px var(--d-stamp);transform:rotate(-3deg);transform-origin:center;animation:lpd-stamp .55s cubic-bezier(.3,1.5,.5,1) 2s both}
.lpd-stamp svg{flex:none;width:20px;height:20px}
.lpd-stamp b{display:block;font:800 13px/1 var(--font-display),ui-sans-serif,system-ui,sans-serif;letter-spacing:.07em;text-transform:uppercase}
.lpd-stamp em{display:block;margin-top:3px;font:500 12px/1 var(--font-text),ui-sans-serif,system-ui,sans-serif;font-style:normal;letter-spacing:.02em;opacity:.9}
@keyframes lpd-in{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}
@keyframes lpd-grow{from{transform:scaleX(0)}to{transform:scaleX(1)}}
@keyframes lpd-sheen{0%,60%{transform:translateX(-120%)}100%{transform:translateX(120%)}}
@keyframes lpd-flow{to{stroke-dashoffset:-26}}
@keyframes lpd-ping{0%{transform:scale(1);opacity:.55}80%,100%{transform:scale(2.6);opacity:0}}
@keyframes lpd-stamp{0%{opacity:0;transform:rotate(-12deg) scale(1.9)}60%{opacity:1;transform:rotate(-3deg) scale(.94)}80%{transform:rotate(-3deg) scale(1.03)}100%{opacity:1;transform:rotate(-3deg) scale(1)}}
@media (prefers-reduced-motion:reduce){.lpd *,.lpd-panel{animation:none!important}}
`;

const ShieldCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.2 7.5 9.5 4.4-1.3 7.5-4.9 7.5-9.5V6L12 3Z" />
    <path d="m8.6 12.2 2.4 2.4 4.4-4.9" />
  </svg>
);

export function HeroDashboard({ ariaLabel = 'Illustration of a team dashboard', title, caption, views = [], selected = 0, lead, routeLabel, feed, stamp }: Props) {
  const [on, setOn] = useState(Math.min(Math.max(0, selected), Math.max(0, views.length - 1)));
  const view = views[on];
  const rows = view?.rows ?? [];
  const dests = rows.slice(0, 3);
  // route ends at rows 1/3, 2/3 and 3/3 of the destination column (y in a 0-100 box): centres of three equal rows
  const ys = [16.7, 50, 83.3];

  return (
    <div className="lpd mx-auto hidden w-full min-w-0 max-w-[860px] lg:block" role="group" aria-label={ariaLabel}>
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <div className="lpd-panel">
        <div className="lpd-head">
          <div className="lpd-title"><span className="lpd-glyph" aria-hidden="true"><i /><i /><i /><i /></span><span>{nb(title)}</span></div>
          <div className="lpd-seg" role="group" aria-label="View by">
            {views.map((v, i) => (
              <button key={v.label} type="button" aria-pressed={i === on} onClick={() => setOn(i)}>{v.label}</button>
            ))}
          </div>
        </div>

        <div className="lpd-body">
          <div className="lpd-bars" key={`bars-${on}`}>
            {caption && <p className="lpd-cap">{nb(caption)}</p>}
            {rows.map((r, i) => (
              <div className="lpd-row" key={r.name}>
                <span className="lpd-name">{nb(r.name)}</span>
                <span className="lpd-track" aria-hidden="true">
                  <span className="lpd-fill" style={{ width: `${Math.min(100, Math.max(0, r.level))}%`, animationDelay: `${0.35 + i * 0.16}s` }} />
                </span>
              </div>
            ))}
          </div>

          <div className="lpd-route">
            <div className="lpd-rgrid">
              <div className="lpd-lead">
                <b><span className="d" aria-hidden="true" />{nb(lead?.title)}</b>
                {lead?.detail && <em>{nb(lead.detail)}</em>}
              </div>
              <svg className="lpd-svg" viewBox="0 0 40 100" preserveAspectRatio="none" aria-hidden="true">
                {ys.slice(0, dests.length).map((y, i) => (
                  <path key={i} className={i === 0 ? 'lpd-live' : 'lpd-line'} d={`M0 50 C 20 50, 20 ${y}, 40 ${y}`} />
                ))}
              </svg>
              <div className="lpd-dests">
                {dests.map((d, i) => (
                  <div className={`lpd-dest${i === 0 ? ' on' : ''}`} key={d.name}><span>{nb(d.name)}</span></div>
                ))}
              </div>
            </div>
            {routeLabel && <p className="lpd-rcap">{nb(routeLabel)}</p>}
          </div>
        </div>

        <div className="lpd-foot">
          <div className="lpd-feed"><i aria-hidden="true" /><span>{nb(feed)}</span></div>
          {stamp?.text && (
            <div className="lpd-stamp">
              <ShieldCheck />
              <span><b>{nb(stamp.text)}</b>{stamp.muted && <em>{nb(stamp.muted)}</em>}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
